/**
 * PublicPrintMediaPage - Página pública para artigos de Jornal/Revista
 *
 * Como artigos de mídia impressa não têm URL original (são uploads),
 * esta página serve como a "visualização original" mostrando:
 * - A imagem da página do jornal/revista em tamanho grande
 * - O conteúdo extraído via OCR ao lado
 * - Informações do veículo e data
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReactComponent as MensureLogo } from '../assets/logo.svg';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Breadcrumbs,
  Link,
  Grid,
  Dialog,
  DialogContent,
  Snackbar
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Share as ShareIcon,
  Home as HomeIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Article as ArticleIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Fullscreen as FullscreenIcon,
  Close as CloseIcon,
  RestartAlt as RestartAltIcon,
  Newspaper as NewspaperIcon,
  MenuBook as MenuBookIcon,
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon
} from '@mui/icons-material';
import api from '../services/api';
import { useTheme } from '../design-system/providers/ThemeProvider';
import CleanContentRenderer from '../components/atoms/CleanContentRenderer/CleanContentRenderer';
import ArticleShareMenu from '../components/molecules/ArticleShareMenu';
import { decodeHtmlEntities } from '../utils/contentProcessor';

const PublicPrintMediaPage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageZoom, setImageZoom] = useState(100);
  const [fullscreenOpen, setFullscreenOpen] = useState(false);
  const [fullscreenZoom, setFullscreenZoom] = useState(100);
  const [shareMenuAnchor, setShareMenuAnchor] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [currentPageIndex, setCurrentPageIndex] = useState(0); // Para navegação de múltiplas páginas

  const { currentTheme } = useTheme();

  // Calcular páginas disponíveis (usa article.pages se existir, senão usa article.image)
  const getPages = () => {
    if (!article) return [];
    if (article.pages && article.pages.length > 0) {
      return article.pages;
    }
    // Fallback para artigo com única página
    if (article.image) {
      return [{ pageNumber: 1, image: article.image }];
    }
    return [];
  };

  const pages = article ? getPages() : [];
  const hasMultiplePages = pages.length > 1;
  const currentPage = pages[currentPageIndex] || pages[0];
  const currentImage = currentPage?.image || article?.image;

  useEffect(() => {
    if (articleId) {
      fetchArticle();
    }
  }, [articleId]);

  const fetchArticle = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/articles/public/${articleId}`);
      setArticle(response.data.data);
    } catch (error) {
      console.error('Erro ao carregar artigo:', error);
      setError('Artigo não encontrado ou não está disponível publicamente');
    } finally {
      setLoading(false);
    }
  };

  // Funções de compartilhamento
  const handleShareMenuOpen = (event) => {
    setShareMenuAnchor(event.currentTarget);
  };

  const handleShareMenuClose = () => {
    setShareMenuAnchor(null);
  };

  const showSnackbar = (message) => {
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleZoomIn = () => {
    setImageZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setImageZoom(prev => Math.max(prev - 25, 50));
  };

  const handleFullscreen = () => {
    setFullscreenZoom(50); // Abre em 50% para boa leitura inicial
    setFullscreenOpen(true);
  };

  const handleFullscreenZoomIn = () => {
    setFullscreenZoom(prev => Math.min(prev + 25, 300));
  };

  const handleFullscreenZoomOut = () => {
    setFullscreenZoom(prev => Math.max(prev - 25, 25));
  };

  const handleFullscreenZoomReset = () => {
    setFullscreenZoom(100);
  };

  const getMediaIcon = () => {
    if (article?.mediaType === 'Revista') {
      return <MenuBookIcon sx={{ fontSize: 40, color: currentTheme.palette.primary.main }} />;
    }
    return <NewspaperIcon sx={{ fontSize: 40, color: currentTheme.palette.primary.main }} />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Data não informada';
    try {
      return new Date(dateString).toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        timeZone: 'UTC'
      });
    } catch {
      return dateString;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.default'
        }}
      >
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error || !article) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: 'background.default',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <Box sx={{ textAlign: 'center', maxWidth: 400, px: 3 }}>
          <Box
            component={MensureLogo}
            aria-hidden
            sx={{
              width: { xs: 118, sm: 136 },
              height: 'auto',
              mb: 3,
              color: 'text.primary',
              opacity: 0.9
            }}
          />
          <Typography variant="h4" fontWeight={800} color="text.primary" gutterBottom>
            Artigo não encontrado
          </Typography>
          <Typography variant="body1" color="text.secondary">
            O artigo que você está procurando não existe ou não está disponível publicamente.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header */}
      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          background: currentTheme.palette.primary.main,
          color: currentTheme.palette.primary.contrastText
        }}
      >
        <Container maxWidth="xl">
          <Breadcrumbs sx={{ mb: 2, color: 'inherit', '& .MuiBreadcrumbs-separator': { color: 'inherit' } }}>
            <Link
              color="inherit"
              href="/"
              sx={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}
            >
              <HomeIcon sx={{ mr: 0.5, fontSize: 20 }} />
              Mensure Monitor
            </Link>
            <Typography color="inherit">
              {article.mediaType === 'Revista' ? 'Revista' : 'Jornal'}
            </Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {getMediaIcon()}
              <Box>
                <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
                  {article.siteName || (article.mediaType === 'Revista' ? 'Revista' : 'Jornal')}
                </Typography>
                {(article.date || article.publishedAt || article.createdAt) && (
                  <Typography variant="body1" sx={{ opacity: 0.9 }}>
                    {formatDate(article.date || article.publishedAt || article.createdAt)}
                  </Typography>
                )}
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Compartilhar">
                <IconButton
                  onClick={handleShareMenuOpen}
                  sx={{ color: 'inherit' }}
                >
                  <ShareIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="xl" sx={{ pb: 4 }}>
        <Grid container spacing={3}>
          {/* Coluna da Imagem - Página do Jornal/Revista */}
          <Grid item xs={12} lg={6}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                border: 1,
                borderColor: 'divider',
                position: 'sticky',
                top: 20
              }}
            >
              {/* Controles de Zoom */}
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 2,
                pb: 2,
                borderBottom: 1,
                borderColor: 'divider'
              }}>
                <Typography variant="subtitle1" fontWeight={600} color="primary">
                  {article.mediaType === 'Revista' ? 'Página da Revista' : 'Página do Jornal'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="Diminuir zoom">
                    <IconButton size="small" onClick={handleZoomOut} disabled={imageZoom <= 50}>
                      <ZoomOutIcon />
                    </IconButton>
                  </Tooltip>
                  <Chip
                    label={`${imageZoom}%`}
                    size="small"
                    variant="outlined"
                    sx={{ minWidth: 60 }}
                  />
                  <Tooltip title="Aumentar zoom">
                    <IconButton size="small" onClick={handleZoomIn} disabled={imageZoom >= 200}>
                      <ZoomInIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Tela cheia">
                    <IconButton size="small" onClick={handleFullscreen}>
                      <FullscreenIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Área da Imagem com Marcadores */}
              <Box
                sx={{
                  overflow: 'auto',
                  maxHeight: 'calc(100vh - 300px)',
                  bgcolor: 'grey.100',
                  borderRadius: 1,
                  p: 2,
                  display: 'flex',
                  justifyContent: 'center'
                }}
              >
                {currentImage ? (
                  <Box sx={{
                    position: 'relative',
                    display: 'block',
                    // Aplicar zoom no container para que os marcadores acompanhem
                    width: `${imageZoom}%`,
                    maxWidth: 'none',
                    mx: 'auto'
                  }}>
                    <img
                      src={currentImage}
                      alt={`Página ${currentPageIndex + 1} de ${article.siteName || article.mediaType}`}
                      style={{
                        width: '100%',
                        height: 'auto',
                        cursor: 'zoom-in',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        borderRadius: 4,
                        display: 'block',
                        objectFit: 'initial'
                      }}
                      onClick={handleFullscreen}
                    />
                    {/* Renderizar marcadores da página atual */}
                    {currentPage?.markers?.map((marker, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          position: 'absolute',
                          left: `${marker.coordinates?.x || 0}%`,
                          top: `${marker.coordinates?.y || 0}%`,
                          width: `${marker.coordinates?.width || 0}%`,
                          height: `${marker.coordinates?.height || 0}%`,
                          backgroundColor: marker.type === 'ocr'
                            ? 'rgba(33, 150, 243, 0.15)'
                            : `${marker.color || '#FFEB3B'}40`,
                          border: marker.type === 'ocr'
                            ? '2px dashed #2196f3'
                            : `2px solid ${marker.color || '#FBC02D'}`,
                          boxSizing: 'border-box',
                          pointerEvents: 'none',
                          borderRadius: marker.type === 'highlight' ? 1 : 0
                        }}
                      />
                    ))}
                    {/* Fallback: renderizar cropArea legado se existir e não houver markers */}
                    {!currentPage?.markers?.length && currentPage?.cropArea && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${currentPage.cropArea.x || 0}%`,
                          top: `${currentPage.cropArea.y || 0}%`,
                          width: `${currentPage.cropArea.width || 0}%`,
                          height: `${currentPage.cropArea.height || 0}%`,
                          backgroundColor: 'rgba(33, 150, 243, 0.15)',
                          border: '2px dashed #2196f3',
                          boxSizing: 'border-box',
                          pointerEvents: 'none'
                        }}
                      />
                    )}
                    {/* Marcadores do artigo principal (página única) */}
                    {!hasMultiplePages && article?.markers?.map((marker, idx) => (
                      <Box
                        key={idx}
                        sx={{
                          position: 'absolute',
                          left: `${marker.coordinates?.x || 0}%`,
                          top: `${marker.coordinates?.y || 0}%`,
                          width: `${marker.coordinates?.width || 0}%`,
                          height: `${marker.coordinates?.height || 0}%`,
                          backgroundColor: marker.type === 'ocr'
                            ? 'rgba(33, 150, 243, 0.15)'
                            : `${marker.color || '#FFEB3B'}40`,
                          border: marker.type === 'ocr'
                            ? '2px dashed #2196f3'
                            : `2px solid ${marker.color || '#FBC02D'}`,
                          boxSizing: 'border-box',
                          pointerEvents: 'none',
                          borderRadius: marker.type === 'highlight' ? 1 : 0
                        }}
                      />
                    ))}
                    {/* Fallback: cropArea do artigo principal */}
                    {!hasMultiplePages && !article?.markers?.length && article?.cropArea && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${article.cropArea.x || 0}%`,
                          top: `${article.cropArea.y || 0}%`,
                          width: `${article.cropArea.width || 0}%`,
                          height: `${article.cropArea.height || 0}%`,
                          backgroundColor: 'rgba(33, 150, 243, 0.15)',
                          border: '2px dashed #2196f3',
                          boxSizing: 'border-box',
                          pointerEvents: 'none'
                        }}
                      />
                    )}
                  </Box>
                ) : (
                  <Box sx={{
                    p: 4,
                    textAlign: 'center',
                    color: 'text.secondary'
                  }}>
                    <ArticleIcon sx={{ fontSize: 64, opacity: 0.5, mb: 2 }} />
                    <Typography>Imagem não disponível</Typography>
                  </Box>
                )}
              </Box>

              {/* Navegação de Páginas */}
              {hasMultiplePages && (
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 2,
                  mt: 2,
                  p: 1,
                  bgcolor: 'background.default',
                  borderRadius: 1
                }}>
                  <IconButton
                    onClick={() => setCurrentPageIndex(prev => Math.max(0, prev - 1))}
                    disabled={currentPageIndex === 0}
                    size="small"
                  >
                    <PrevIcon />
                  </IconButton>

                  {/* Miniaturas das páginas */}
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {pages.map((page, index) => (
                      <Box
                        key={index}
                        onClick={() => setCurrentPageIndex(index)}
                        sx={{
                          width: 40,
                          height: 56,
                          border: '2px solid',
                          borderColor: currentPageIndex === index ? 'primary.main' : 'divider',
                          borderRadius: 0.5,
                          overflow: 'hidden',
                          cursor: 'pointer',
                          opacity: currentPageIndex === index ? 1 : 0.7,
                          transition: 'all 0.2s',
                          '&:hover': { opacity: 1, borderColor: 'primary.light' }
                        }}
                      >
                        <img
                          src={page.image}
                          alt={`Página ${page.pageNumber}`}
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    ))}
                  </Box>

                  <IconButton
                    onClick={() => setCurrentPageIndex(prev => Math.min(pages.length - 1, prev + 1))}
                    disabled={currentPageIndex === pages.length - 1}
                    size="small"
                  >
                    <NextIcon />
                  </IconButton>

                  <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                    Página {currentPageIndex + 1} de {pages.length}
                  </Typography>
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Coluna do Conteúdo Extraído */}
          <Grid item xs={12} lg={6}>
            <Paper elevation={1} sx={{ p: 4, border: 1, borderColor: 'divider' }}>
              {/* Título da Matéria */}
              <Tooltip title={article.title} arrow placement="top">
                <Typography
                  variant="h4"
                  component="h2"
                  gutterBottom
                  sx={{
                    fontWeight: 600,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: '2',
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.0,
                    cursor: 'help',
                    maxWidth: { xs: '100%', md: '95%' }, // Deixar título usar quase toda largura
                    fontSize: { xs: '3rem !important', sm: '4.5rem !important', md: '6rem !important' }, // Fonte gigante
                    letterSpacing: '-0.03em'
                  }}
                >
                  {decodeHtmlEntities(article.title)}
                </Typography>
              </Tooltip>

              {/* Informações do Artigo */}
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 2,
                mb: 3,
                pb: 2,
                borderBottom: 1,
                borderColor: 'divider'
              }}>
                {article.author && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {article.author}
                    </Typography>
                  </Box>
                )}
                {article.date && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <ScheduleIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(article.date)}
                    </Typography>
                  </Box>
                )}
                <Chip
                  icon={article.mediaType === 'Revista' ? <MenuBookIcon /> : <NewspaperIcon />}
                  label={article.mediaType}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>

              {/* Descrição/Resumo */}
              {article.description && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Resumo
                  </Typography>
                  <Typography variant="body1" paragraph sx={{
                    bgcolor: 'action.hover',
                    p: 2,
                    borderRadius: 1,
                    fontStyle: 'italic'
                  }}>
                    {article.description}
                  </Typography>
                </Box>
              )}

              {/* Conteúdo Extraído */}
              <Box sx={{ mb: 4 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Conteúdo da Matéria
                </Typography>

                {article.content ? (
                  <CleanContentRenderer
                    contentBlocks={Array.isArray(article.content) ? article.content : null}
                    htmlContent={!Array.isArray(article.content) ? article.content : null}
                    allImages={[]}
                    mediaType={article.mediaType}
                    contentType={article.contentType}
                    sx={{
                      lineHeight: 1.8,
                      '& p': { mb: 2 },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        mt: 3,
                        mb: 2,
                        color: 'primary.main'
                      }
                    }}
                  />
                ) : (
                  <Typography variant="body1" color="text.secondary">
                    Conteúdo não disponível
                  </Typography>
                )}
              </Box>

              {/* Tags */}
              {article.tags && article.tags.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Tags
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {article.tags.map((tag, index) => (
                      <Chip
                        key={index}
                        label={tag.name || tag}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Informações Adicionais */}
              <Paper variant="outlined" sx={{ p: 2, bgcolor: 'background.default' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <InfoIcon fontSize="small" color="action" />
                  <Typography variant="subtitle2" color="text.secondary">
                    Sobre este conteúdo
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Este conteúdo foi extraído de uma página de {article.mediaType?.toLowerCase() || 'mídia impressa'}
                  {article.siteName ? ` (${article.siteName})` : ''} utilizando tecnologia de reconhecimento
                  óptico de caracteres (OCR) com inteligência artificial.
                </Typography>
              </Paper>
            </Paper>

            {/* Botões de Ação */}
            <Paper elevation={1} sx={{ p: 3, mt: 3, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Button
                  variant="contained"
                  fullWidth
                  startIcon={<ShareIcon />}
                  onClick={handleShareMenuOpen}
                >
                  Compartilhar
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate(-1)}
                >
                  Voltar
                </Button>
              </Box>
            </Paper>

            {/* Footer */}
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Digitalizado e processado via Mensure Monitor
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>

      {/* Dialog de Tela Cheia com Zoom */}
      <Dialog
        open={fullscreenOpen}
        onClose={() => setFullscreenOpen(false)}
        maxWidth={false}
        fullScreen
        PaperProps={{
          sx: { bgcolor: 'grey.900' }
        }}
      >
        <DialogContent sx={{
          p: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          overflow: 'auto'
        }}>
          {/* Barra de Controles Superior */}
          <Box sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 2,
            py: 1.5,
            bgcolor: 'rgba(0,0,0,0.8)',
            backdropFilter: 'blur(10px)',
            zIndex: 10
          }}>
            {/* Informações do Artigo */}
            <Box sx={{ color: 'white' }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {article.siteName || article.mediaType}
              </Typography>
              {article.date && (
                <Typography variant="body2" sx={{ opacity: 0.7 }}>
                  {formatDate(article.date)}
                </Typography>
              )}
            </Box>

            {/* Controles de Zoom */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Tooltip title="Diminuir zoom (-)">
                <IconButton
                  onClick={handleFullscreenZoomOut}
                  disabled={fullscreenZoom <= 25}
                  sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  <ZoomOutIcon />
                </IconButton>
              </Tooltip>

              <Chip
                label={`${fullscreenZoom}%`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.15)',
                  color: 'white',
                  minWidth: 70,
                  fontWeight: 600
                }}
              />

              <Tooltip title="Aumentar zoom (+)">
                <IconButton
                  onClick={handleFullscreenZoomIn}
                  disabled={fullscreenZoom >= 300}
                  sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  <ZoomInIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Resetar zoom (100%)">
                <IconButton
                  onClick={handleFullscreenZoomReset}
                  sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  <RestartAltIcon />
                </IconButton>
              </Tooltip>

              <Box sx={{ width: 1, height: 24, bgcolor: 'rgba(255,255,255,0.3)', mx: 1 }} />

              <Tooltip title="Fechar (Esc)">
                <IconButton
                  onClick={() => setFullscreenOpen(false)}
                  sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
                >
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          {/* Área da Imagem com Scroll e Marcadores */}
          <Box
            sx={{
              width: '100%',
              height: '100%',
              overflow: 'auto',
              display: 'flex',
              alignItems: fullscreenZoom <= 100 ? 'center' : 'flex-start',
              justifyContent: fullscreenZoom <= 100 ? 'center' : 'flex-start',
              pt: 8, // Espaço para a barra de controles
              pb: hasMultiplePages ? 10 : 2, // Espaço extra para navegação de páginas
              px: 2
            }}
          >
            {currentImage && (
              <Box
                sx={{
                  position: 'relative',
                  display: 'block',
                  // Aplicar zoom no container para que os marcadores acompanhem perfeitamente
                  width: `${fullscreenZoom}%`,
                  maxWidth: 'none',
                  margin: 'auto'
                }}
              >
                <img
                  src={currentImage}
                  alt={`Página ${currentPageIndex + 1} de ${article.siteName || article.mediaType}`}
                  style={{
                    width: '100%',
                    height: 'auto',
                    transition: 'width 0.2s ease',
                    cursor: fullscreenZoom > 100 ? 'grab' : 'zoom-in',
                    display: 'block',
                    objectFit: 'initial'
                  }}
                  onClick={() => {
                    if (fullscreenZoom < 200) {
                      setFullscreenZoom(prev => Math.min(prev + 50, 300));
                    }
                  }}
                />
                {/* Marcadores em fullscreen - página atual */}
                {currentPage?.markers?.map((marker, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      position: 'absolute',
                      left: `${marker.coordinates?.x || 0}%`,
                      top: `${marker.coordinates?.y || 0}%`,
                      width: `${marker.coordinates?.width || 0}%`,
                      height: `${marker.coordinates?.height || 0}%`,
                      backgroundColor: marker.type === 'ocr'
                        ? 'rgba(33, 150, 243, 0.2)'
                        : `${marker.color || '#FFEB3B'}50`,
                      border: marker.type === 'ocr'
                        ? '3px dashed #2196f3'
                        : `3px solid ${marker.color || '#FBC02D'}`,
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                      borderRadius: marker.type === 'highlight' ? 1 : 0
                    }}
                  />
                ))}
                {/* Fallback: cropArea legado */}
                {!currentPage?.markers?.length && currentPage?.cropArea && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${currentPage.cropArea.x || 0}%`,
                      top: `${currentPage.cropArea.y || 0}%`,
                      width: `${currentPage.cropArea.width || 0}%`,
                      height: `${currentPage.cropArea.height || 0}%`,
                      backgroundColor: 'rgba(33, 150, 243, 0.2)',
                      border: '3px dashed #2196f3',
                      boxSizing: 'border-box',
                      pointerEvents: 'none'
                    }}
                  />
                )}
                {/* Marcadores do artigo principal (página única) */}
                {!hasMultiplePages && article?.markers?.map((marker, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      position: 'absolute',
                      left: `${marker.coordinates?.x || 0}%`,
                      top: `${marker.coordinates?.y || 0}%`,
                      width: `${marker.coordinates?.width || 0}%`,
                      height: `${marker.coordinates?.height || 0}%`,
                      backgroundColor: marker.type === 'ocr'
                        ? 'rgba(33, 150, 243, 0.2)'
                        : `${marker.color || '#FFEB3B'}50`,
                      border: marker.type === 'ocr'
                        ? '3px dashed #2196f3'
                        : `3px solid ${marker.color || '#FBC02D'}`,
                      boxSizing: 'border-box',
                      pointerEvents: 'none',
                      borderRadius: marker.type === 'highlight' ? 1 : 0
                    }}
                  />
                ))}
                {/* Fallback: cropArea do artigo principal */}
                {!hasMultiplePages && !article?.markers?.length && article?.cropArea && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: `${article.cropArea.x || 0}%`,
                      top: `${article.cropArea.y || 0}%`,
                      width: `${article.cropArea.width || 0}%`,
                      height: `${article.cropArea.height || 0}%`,
                      backgroundColor: 'rgba(33, 150, 243, 0.2)',
                      border: '3px dashed #2196f3',
                      boxSizing: 'border-box',
                      pointerEvents: 'none'
                    }}
                  />
                )}
              </Box>
            )}
          </Box>

          {/* Navegação de páginas no fullscreen */}
          {hasMultiplePages && (
            <Box sx={{
              position: 'fixed',
              bottom: 60,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              bgcolor: 'rgba(0,0,0,0.8)',
              px: 2,
              py: 1,
              borderRadius: 2
            }}>
              <IconButton
                onClick={() => setCurrentPageIndex(prev => Math.max(0, prev - 1))}
                disabled={currentPageIndex === 0}
                sx={{ color: 'white' }}
                size="small"
              >
                <PrevIcon />
              </IconButton>

              {pages.map((page, index) => (
                <Box
                  key={index}
                  onClick={() => setCurrentPageIndex(index)}
                  sx={{
                    width: 30,
                    height: 42,
                    border: '2px solid',
                    borderColor: currentPageIndex === index ? 'primary.main' : 'rgba(255,255,255,0.3)',
                    borderRadius: 0.5,
                    overflow: 'hidden',
                    cursor: 'pointer',
                    opacity: currentPageIndex === index ? 1 : 0.6,
                    '&:hover': { opacity: 1 }
                  }}
                >
                  <img
                    src={page.image}
                    alt={`Pág ${page.pageNumber}`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                </Box>
              ))}

              <IconButton
                onClick={() => setCurrentPageIndex(prev => Math.min(pages.length - 1, prev + 1))}
                disabled={currentPageIndex === pages.length - 1}
                sx={{ color: 'white' }}
                size="small"
              >
                <NextIcon />
              </IconButton>

              <Typography variant="body2" sx={{ color: 'white', ml: 1 }}>
                {currentPageIndex + 1}/{pages.length}
              </Typography>
            </Box>
          )}

          {/* Dica de uso */}
          <Box sx={{
            position: 'fixed',
            bottom: 16,
            left: '50%',
            transform: 'translateX(-50%)',
            bgcolor: 'rgba(0,0,0,0.7)',
            color: 'white',
            px: 2,
            py: 1,
            borderRadius: 2,
            fontSize: 12,
            opacity: 0.8
          }}>
            {hasMultiplePages
              ? 'Use as setas ou clique nas miniaturas para navegar entre páginas'
              : 'Clique na imagem ou use os controles acima para ajustar o zoom'
            }
          </Box>
        </DialogContent>
      </Dialog>

      <ArticleShareMenu
        anchorEl={shareMenuAnchor}
        article={article}
        originalUrl={article?.siteUrl || article?.url || article?.videoUrl}
        publicUrl={typeof window !== 'undefined' ? window.location.href : ''}
        onClose={handleShareMenuClose}
        onNotify={showSnackbar}
      />

      {/* Snackbar de confirmação */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default PublicPrintMediaPage;
